import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { absoluteUrl } from "@/lib/site";
import { supabase } from "@/integrations/supabase/client";
import { getBeat, getMyBeatAccount, recordBeatEvent, toggleBeatFavorite } from "@/lib/bkls.functions";
import { beatJsonLd, breadcrumbJsonLd } from "@/lib/bkls-rights";
import {
  beatsCatalogQuery,
  beatsSettingsQuery,
  beatsTiersQuery,
  beatWaveformQuery,
} from "@/lib/bkls-queries";
import { formatMoney, tierAvailableFor, tierPriceFor, UNIVERSAL_RESTRICTIONS } from "@/lib/bkls";
import { beatMetaLine, relatedBeats, topicLabels, type MarketplaceBeat } from "@/lib/bkls-marketplace";
import { BeatCard } from "@/components/beats/BeatCard";
import { BeatLicenseCheckout } from "@/components/beats/BeatLicenseCheckout";
import { beatTierCta } from "@/lib/bkls-cta";
import { WaveformPlayer } from "@/components/beats/WaveformPlayer";
import { track } from "@/lib/analytics";

const beatQuery = (slug: string) =>
  queryOptions({
    queryKey: ["bkls", "beat", slug],
    queryFn: () => getBeat({ data: { slug } }),
  });

export const Route = createFileRoute("/beats/$slug")({
  // `tier` lets sign-in return the visitor to the exact tier they picked.
  validateSearch: (search: Record<string, unknown>): { tier?: string } => ({
    tier: typeof search.tier === "string" && search.tier.length <= 60 ? search.tier : undefined,
  }),
  loader: async ({ context, params }) => {
    const beat = await context.queryClient.ensureQueryData(beatQuery(params.slug));
    if (!beat) throw notFound();
    // Hydrate every query the component renders so SSR and the client's
    // first render agree.
    await Promise.all([
      context.queryClient.ensureQueryData(beatsCatalogQuery),
      context.queryClient.ensureQueryData(beatsTiersQuery),
      context.queryClient.ensureQueryData(beatsSettingsQuery),
      context.queryClient.ensureQueryData(beatWaveformQuery(params.slug)),
    ]);
    return { beat };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Beat unavailable · Buddha KA" }, { name: "robots", content: "noindex" }] };
    }
    const { beat } = loaderData;
    const url = absoluteUrl(`/beats/${beat.slug}`);
    // Signed storage URLs expire; metadata must use the stable redirect route.
    const imageUrl = beat.artworkUrl ? absoluteUrl(`/api/public/beat-art/${beat.slug}`) : null;
    const description =
      beat.subtitle ?? beat.description?.slice(0, 150) ?? `License ${beat.title} — original Buddha KA production.`;
    // Only genuinely public, fixed-price, available tiers become Offers.
    const offers = (beat.prices ?? [])
      .filter((p: { available?: boolean | null; price_cents?: number | null }) => p.available !== false && (p.price_cents ?? 0) > 0)
      .map((p: { tier_key: string; price_cents?: number | null }) => ({
        "@type": "Offer",
        name: p.tier_key,
        price: ((p.price_cents ?? 0) / 100).toFixed(2),
        priceCurrency: "USD",
        url,
        availability: "https://schema.org/InStock",
      }));
    const recording: Record<string, unknown> = {
      ...beatJsonLd({
        slug: beat.slug,
        title: beat.title,
        description,
        url,
        imageUrl,
        genre: beat.genre,
        bpm: beat.bpm,
        durationSeconds: beat.duration_seconds,
      }),
      ...(offers.length ? { offers } : {}),
    };
    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `What do I get when I license ${beat.title}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: "You receive a written licence and the audio formats listed on the tier you choose. Every transaction grants a licence only — copyright is never transferred.",
          },
        },
        {
          "@type": "Question",
          name: "Can I resell or redistribute the beat?",
          acceptedAnswer: {
            "@type": "Answer",
            text: UNIVERSAL_RESTRICTIONS.join(" "),
          },
        },
      ],
    };
    return {
      meta: [
        { title: `${beat.title} · Beat Licensing · Buddha KA` },
        { name: "description", content: description },
        { property: "og:title", content: `${beat.title} · Buddha KA Beats` },
        { property: "og:description", content: description },
        { property: "og:type", content: "music.song" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(imageUrl
          ? [
              { property: "og:image", content: imageUrl },
              { name: "twitter:image", content: imageUrl },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(recording) },
        { type: "application/ld+json", children: JSON.stringify(faq) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Beats", url: absoluteUrl("/beats") },
              { name: "Catalog", url: absoluteUrl("/beats/catalog") },
              { name: beat.title, url },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: BeatMissing,
  errorComponent: BeatMissing,
  component: BeatDetail,
});

function BeatMissing() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
      <h1 className="font-serif text-4xl">That beat is not available.</h1>
      <Link to="/beats/catalog" className="mt-6 inline-flex text-sm text-gold hover:text-gold-luminous">
        ← Back to the catalog
      </Link>
    </div>
  );
}

/** Tiers the signed-in member already holds for this beat. */
function useOwnedTiers(slug: string) {
  const [userId, setUserId] = useState<string | null>(null);
  const fetchAccount = useServerFn(getMyBeatAccount);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => setUserId(session?.user?.id ?? null));
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const account = useQuery({
    queryKey: ["bkls", "account", userId ?? "anon"],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: () => fetchAccount({ data: {} } as never),
  });

  const owned = new Set(
    (account.data?.licenses ?? [])
      .filter((l) => l.beat_slug === slug && l.status === "active")
      .map((l) => l.tier_key),
  );
  return { signedIn: Boolean(userId), owned };
}

function BeatDetail() {
  const { slug } = Route.useParams();
  const { tier: preselectedTier } = Route.useSearch();
  const { data: beat } = useQuery(beatQuery(slug));
  const tiers = useQuery(beatsTiersQuery);
  const settings = useQuery(beatsSettingsQuery);
  const catalog = useQuery(beatsCatalogQuery);
  const waveform = useQuery(beatWaveformQuery(slug));
  const { signedIn, owned } = useOwnedTiers(slug);
  const [notice, setNotice] = useState<string | null>(null);
  // Which tier's checkout panel is open. Seeded from ?tier= so a sign-in
  // round-trip reopens the tier the visitor chose.
  const [activeTier, setActiveTier] = useState<string | null>(preselectedTier ?? null);
  const [saved, setSaved] = useState(false);
  const trackEvent = useServerFn(recordBeatEvent);
  const toggleFavorite = useServerFn(toggleBeatFavorite);
  const seen = useRef<Set<string>>(new Set());

  // One event per interaction per mount — hydration and rerenders never
  // double-count.
  function record(eventType: "view" | "preview_play" | "preview_complete" | "license_click" | "checkout_start", tierKey?: string) {
    const dedupe = `${eventType}:${tierKey ?? ""}`;
    if (seen.current.has(dedupe)) return;
    seen.current.add(dedupe);
    void trackEvent({ data: { beatSlug: slug, eventType, ...(tierKey ? { tierKey } : {}) } }).catch(() => {});
  }

  useEffect(() => {
    if (!beat) return;
    record("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat?.slug]);

  async function saveToWishlist() {
    try {
      const result = await toggleFavorite({ data: { slug, kind: "beat_wishlist" } });
      setSaved(result.saved);
      setNotice(result.saved ? "Saved to your wishlist." : "Removed from your wishlist.");
    } catch {
      setNotice("Sign in to save this beat to your wishlist.");
    }
  }

  if (!beat) return <BeatMissing />;

  const rows = catalog.data ?? [];
  const self = rows.find((b) => b.slug === slug);
  const marketplaceBeat: MarketplaceBeat =
    self ?? { ...beat, collectionSlug: null, publishedAt: null, updatedAt: null, topics: [] };
  const related = self ? relatedBeats(self, rows, 3) : [];
  const topics = topicLabels(marketplaceBeat.topics);

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/beats" className="text-gold hover:text-gold-luminous">
          Beats
        </Link>{" "}
        /{" "}
        <Link to="/beats/catalog" className="text-gold hover:text-gold-luminous">
          Catalog
        </Link>{" "}
        / {beat.title}
      </nav>

      <div className="mt-6 grid gap-10 md:grid-cols-[320px_1fr]">
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border hairline bg-charcoal">
            {beat.artworkUrl && (
              <img
                src={beat.artworkUrl}
                alt={`Cover artwork for the beat ${beat.title}`}
                className="h-full w-full object-cover"
              />
            )}
          </div>
          {beat.previewUrl && (
            <div className="mt-4">
              <WaveformPlayer
                src={beat.previewUrl}
                title={beat.title}
                peaks={waveform.data?.peaks}
                durationSeconds={beat.duration_seconds}
                onPlay={() => record("preview_play")}
                onEnded={() => record("preview_complete")}
              />
            </div>
          )}
          <button
            type="button"
            onClick={saveToWishlist}
            aria-pressed={saved}
            className="mt-4 w-full rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold"
          >
            {saved ? "Saved to wishlist" : "Save to wishlist"}
          </button>
          {signedIn && owned.size > 0 && (
            <Link
              to="/beats/account"
              className="mt-3 inline-flex w-full justify-center rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
            >
              Open your licences
            </Link>
          )}
        </div>

        <div>
          <h1 className="font-serif text-4xl sm:text-5xl">{beat.title}</h1>
          {beat.subtitle && <p className="mt-3 text-muted-foreground">{beat.subtitle}</p>}
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-gold/80">
            {beatMetaLine(marketplaceBeat)}
          </p>
          {marketplaceBeat.collectionSlug && (
            <p className="mt-3 text-sm">
              <Link
                to="/beats/collections/$slug"
                params={{ slug: marketplaceBeat.collectionSlug }}
                className="text-gold hover:text-gold-luminous"
              >
                Part of the {marketplaceBeat.collectionSlug.replace(/-/g, " ")} collection →
              </Link>
            </p>
          )}
          {beat.description && <p className="mt-6 text-sm leading-relaxed text-muted-foreground">{beat.description}</p>}

          {topics.length > 0 && (
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Topics">
              {topics.map((t) => (
                <li key={t.slug}>
                  <Link
                    to="/topics/$slug"
                    params={{ slug: t.slug }}
                    className="rounded-full border hairline px-3 py-1 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                  >
                    {t.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {beat.tags.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2" aria-label="Tags">
              {beat.tags.map((t) => (
                <li key={t}>
                  <Link
                    to="/beats/catalog"
                    search={{ tag: t }}
                    className="rounded-full border hairline px-3 py-1 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                  >
                    {t}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <section className="mt-10" aria-labelledby="license-options">
            <h2 id="license-options" className="font-serif text-2xl text-gold-luminous">
              Licence options
            </h2>
            <div className="mt-4 space-y-3">
              {(tiers.data ?? [])
                .filter((t) => tierAvailableFor(t, beat))
                .map((t) => {
                  const price = tierPriceFor(t, beat);
                  const isOwned = owned.has(t.key);
                  // Quote-only tiers (Custom, Exclusive) never enter
                  // self-service checkout — the server refuses them too.
                  const cta = beatTierCta({
                    tier: t,
                    beat,
                    checkoutEnabled: settings.data?.checkout_enabled,
                    owned: isOwned,
                  });
                  const sellable = cta === "checkout";
                  const isOpen = activeTier === t.key;
                  const priceLabel = t.is_quote_only
                    ? "By quote"
                    : price
                      ? formatMoney(price, t.currency)
                      : "On request";
                  return (
                    <div key={t.key} className="rounded-2xl border hairline bg-charcoal p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-serif text-lg text-ivory">{t.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.file_formats.join(", ")} · {t.term}
                            {t.stream_limit ? ` · ${t.stream_limit} streams` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gold">{priceLabel}</span>
                          {cta === "owned" ? (
                            <Link
                              to="/beats/account"
                              className="inline-flex min-h-11 items-center rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
                            >
                              You own this — download
                            </Link>
                          ) : cta === "request" ? (
                            <Link
                              to="/beats/custom"
                              onClick={() => record("license_click", t.key)}
                              className="inline-flex min-h-11 items-center rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold"
                            >
                              Request
                            </Link>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                record("license_click", t.key);
                                setNotice(null);
                                setActiveTier(isOpen ? null : t.key);
                              }}
                              aria-expanded={isOpen}
                              aria-controls={`beat-license-panel-${t.key}`}
                              className="inline-flex min-h-11 items-center rounded-full bg-gold-gradient px-5 py-2.5 text-xs font-semibold text-obsidian shadow-gold transition-all"
                            >
                              {isOpen ? "Close" : `License · ${priceLabel}`}
                            </button>
                          )}
                        </div>
                      </div>

                      {sellable && isOpen && (
                        <div id={`beat-license-panel-${t.key}`} className="mt-5">
                          <BeatLicenseCheckout
                            slug={slug}
                            tierKey={t.key}
                            tierName={t.name}
                            beatTitle={beat.title}
                            priceLabel={priceLabel}
                            isAuthenticated={signedIn}
                            onCheckoutStart={() => record("checkout_start", t.key)}
                            onCancel={() => setActiveTier(null)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            <p aria-live="polite" role="status" className={notice ? "mt-4 text-sm text-muted-foreground" : "sr-only"}>
              {notice ?? ""}
            </p>
            {!settings.data?.checkout_enabled && (
              <p className="mt-4 text-sm text-muted-foreground">
                Instant checkout is not open yet — licences are issued directly through a request.
              </p>
            )}
            <Link to="/beats/license" className="mt-5 inline-flex text-xs text-gold hover:text-gold-luminous">
              Read the full licence terms →
            </Link>
          </section>

          <section className="mt-10" aria-labelledby="always-true">
            <h2 id="always-true" className="font-serif text-2xl text-gold-luminous">
              True of every tier
            </h2>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              {UNIVERSAL_RESTRICTIONS.map((r) => (
                <li key={r} className="pl-4 -indent-4">
                  — {r}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20" aria-labelledby="related-heading">
          <h2 id="related-heading" className="font-serif text-3xl">
            Close to this one
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Matched on shared collection, genre, mood, tempo and key — nothing else.
          </p>
          <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <li key={r.beat.id}>
                <BeatCard
                  beat={r.beat}
                  tiers={tiers.data ?? []}
                  reasons={r.reasons}
                  onSelect={() => track("beat_related_click", { from: slug, to: r.beat.slug })}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
